import {
    City,
    CompetencePage,
    FetchCities,
    FetchCompetences,
    FetchMethods,
    MethodPage,
} from "utils/interfaces"
import {
    CatalogSearchQuery,
    CatalogSearchQueryKey,
    defaultQuery,
} from "modules/PsyCatalog/common/searchQuery"
import { createStaticPaths, getSearchApiUrl } from "modules/PsyCatalog/ssg/ssg-utils"
import { httpClient } from "utils/infrastructure/httpClient"

export type BindTypes = {
    cities: FetchCities
    methods: FetchMethods
    competences: FetchCompetences
}

export type Collection = keyof BindTypes

const endpoints = new Map<Collection, string>([
    ["cities", `cities`],
    ["methods", `methods`],
    ["competences", `competences`],
])

const searchQueryKeys = new Map<Collection, CatalogSearchQueryKey>([
    ["cities", "city"],
    ["methods", "methods"],
    ["competences", "competences"],
])

export async function getCollection(
    collection: Collection
): Promise<FetchCities | CompetencePage[] | FetchMethods> {
    const data = await httpClient.get<BindTypes[typeof collection]>(endpoints.get(collection) || "")
    if (collection === "competences") {
        return (data as BindTypes["competences"]).flatMap(({ competences }) => competences)
    }
    return data as Exclude<typeof data, FetchCompetences>
}

export function getSearchQueryForCollection(collection: Collection, value: string | number[]) {
    const key = searchQueryKeys.get(collection)
    if (!key) {
        throw new Error(`Invalid search query for collection-type: ${collection}`)
    }
    return { ...defaultQuery, [key]: value }
}

export async function getPagesCount(query: CatalogSearchQuery) {
    const { pages_count } = await httpClient.post<{ pages_count: number }>(getSearchApiUrl(1), {
        ...query,
        profiles_per_page: 10,
        offset_from_utc_minutes: 0,
    })
    return pages_count
}

export async function getPageData(query: CatalogSearchQuery, page: string | number) {
    return await httpClient.post<City | CompetencePage | MethodPage>(getSearchApiUrl(page), {
        ...query,
        profiles_per_page: 10,
        offset_from_utc_minutes: 0,
    })
}

export async function getAllPagesPaths(
    collection: Collection,
    slug: string,
    searchQueryValue: string | number[]
) {
    return createStaticPaths(
        await getPagesCount(getSearchQueryForCollection(collection, searchQueryValue)),
        slug
    )
}
